/**
 * Conectar el WhatsApp Business de Juan desde la página /app/whatsapp del CRM.
 *
 * El navegador solo lanza el registro insertado de Meta y pasa aquí el código
 * que devuelve. El canje por el token, la suscripción y la petición del
 * historial se hacen en el servidor, con el secreto de la app; el token se
 * guarda en `whatsapp_conexion` y nunca vuelve al navegador.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { almacenSupabase } from "@/whatsapp/almacen";
import { conectarWhatsapp, pasosPendientes, type ResultadoConexion } from "@/whatsapp/conexion";
import { graphWhatsapp, VERSION_GRAPH } from "@/whatsapp/graph";
import { requireAdmin } from "./supabase.server";

export interface ConfiguracionWhatsapp {
  /** Lo que necesita el SDK de Facebook en el navegador. Ninguno es secreto. */
  appId: string | null;
  configId: string | null;
  versionGraph: string;
  /** Variables que faltan en el servidor para poder conectar y recibir avisos. */
  faltan: string[];
}

const entorno = (nombre: string) => process.env[nombre]?.trim() || null;

function graph() {
  return graphWhatsapp({
    version: entorno("WHATSAPP_GRAPH_VERSION") ?? undefined,
    baseUrl: entorno("WHATSAPP_GRAPH_BASE_URL") ?? undefined,
  });
}

const sesion = z.object({ accessToken: z.string().min(20) });

export const configuracionWhatsapp = createServerFn({ method: "POST" })
  .validator((input: unknown) => sesion.parse(input))
  .handler(async ({ data }): Promise<ConfiguracionWhatsapp> => {
    await requireAdmin(data.accessToken);
    const faltan = [
      "WHATSAPP_APP_ID",
      "WHATSAPP_CONFIG_ID",
      "WHATSAPP_APP_SECRET",
      "WHATSAPP_VERIFY_TOKEN",
    ].filter((nombre) => !entorno(nombre));
    return {
      appId: entorno("WHATSAPP_APP_ID"),
      configId: entorno("WHATSAPP_CONFIG_ID"),
      versionGraph: entorno("WHATSAPP_GRAPH_VERSION") ?? VERSION_GRAPH,
      faltan,
    };
  });

const idMeta = z
  .string()
  .regex(/^\d{5,25}$/)
  .nullish();

const conexionSchema = sesion.extend({
  // El código caduca a los 30 segundos: se canjea en cuanto llega.
  codigo: z.string().min(10).max(2000),
  wabaId: idMeta,
  phoneNumberId: idMeta,
});

export const conectarWhatsappFn = createServerFn({ method: "POST" })
  .validator((input: unknown) => conexionSchema.parse(input))
  .handler(async ({ data }): Promise<ResultadoConexion> => {
    const { admin } = await requireAdmin(data.accessToken);
    const appId = entorno("WHATSAPP_APP_ID");
    const secreto = entorno("WHATSAPP_APP_SECRET");
    if (!appId || !secreto) throw new Error("Faltan WHATSAPP_APP_ID o WHATSAPP_APP_SECRET");
    try {
      return await conectarWhatsapp(
        { codigo: data.codigo, wabaId: data.wabaId, phoneNumberId: data.phoneNumberId },
        { appId, secreto },
        { graph: graph(), almacen: almacenSupabase(admin) },
      );
    } catch (fallo) {
      // El mensaje de Meta sí (no lleva el token); el código y el secreto, nunca.
      const mensaje = fallo instanceof Error ? fallo.message : "Error inesperado";
      console.error("WhatsApp: conexión fallida", mensaje);
      throw new Error(mensaje);
    }
  });

/** Repite los pasos que fallaron al conectar (suscribir, contactos, historial). */
export const reintentarConexionWhatsapp = createServerFn({ method: "POST" })
  .validator((input: unknown) => sesion.parse(input))
  .handler(async ({ data }): Promise<ResultadoConexion> => {
    const { admin } = await requireAdmin(data.accessToken);
    const almacen = almacenSupabase(admin);
    const conexion = await almacen.conexion();
    if (!conexion) throw new Error("WhatsApp no está conectado");
    return { numero: null, pasos: await pasosPendientes(conexion, { graph: graph(), almacen }) };
  });
